import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize} from 'rxjs/operators';
import {FolderService} from '../../../services/folder/folder.service';
import {NotificationService} from '../../../services/common/notification.service';
import {
  ExportFormat,
  ExportSection,
  FOLDER_ITEM_TYPES,
  Folder,
  FolderExportRequest,
  FolderExportSection
} from '../../../dtos/folder/folder';
import {downloadBlob, filenameFromResponse, folderErrorMessage} from '../../../shared/folder/folder-utils';

export interface FolderExportDialogData {
  folder: Folder;
}

interface SectionSelection {
  section: ExportSection;
  entryCount: number;
  selected: Set<string>;
}

/**
 * exports a Sammelmappe as XLSX or PDF, with an optional selection of the fields per section
 */
@Component({
  selector: 'app-folder-export-dialog',
  templateUrl: './folder-export-dialog.component.html',
  styleUrls: ['./folder-export-dialog.component.scss'],
  standalone: false
})
export class FolderExportDialogComponent implements OnInit {

  format: ExportFormat = 'PDF';
  sections: SectionSelection[] = [];
  loading = true;
  exporting = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: FolderExportDialogData,
              private folderService: FolderService,
              private notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.folderService.getExportFields(this.data.folder.id)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: sections => this.sections = sections
          .map(section => ({
            section,
            entryCount: this.entryCount(section.section),
            selected: new Set(section.fields.filter(f => f.defaultSelected).map(f => f.key))
          }))
          // sections without entries are left out of the export anyway
          .filter(s => s.entryCount > 0),
        error: error => this.notificationService.openSnackBar(folderErrorMessage(error))
      });
  }

  get isEmpty(): boolean {
    return this.data.folder.itemCount === 0;
  }

  get nothingSelected(): boolean {
    return !this.isEmpty && this.sections.length > 0 && this.sections.every(s => s.selected.size === 0);
  }

  toggleField(selection: SectionSelection, key: string, checked: boolean): void {
    if (checked) {
      selection.selected.add(key);
    } else {
      selection.selected.delete(key);
    }
  }

  toggleSection(selection: SectionSelection, checked: boolean): void {
    selection.selected = new Set(checked ? selection.section.fields.map(f => f.key) : []);
  }

  resetDefaults(): void {
    this.sections.forEach(s =>
      s.selected = new Set(s.section.fields.filter(f => f.defaultSelected).map(f => f.key)));
  }

  allSelected(selection: SectionSelection): boolean {
    return selection.selected.size === selection.section.fields.length;
  }

  someSelected(selection: SectionSelection): boolean {
    return selection.selected.size > 0 && !this.allSelected(selection);
  }

  /**
   * opens the PDF in a new tab. The tab is opened before the request, as browsers block
   * popups that are not opened directly in a click handler.
   */
  preview(): void {
    const tab = window.open('', '_blank');
    tab?.document.write('<p style="font-family: sans-serif">PDF wird erstellt …</p>');
    this.export('PDF', response => {
      const url = window.URL.createObjectURL(new Blob([response.body], {type: 'application/pdf'}));
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      // the tab has loaded the pdf by then
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    }, () => tab?.close());
  }

  download(): void {
    const extension = this.format.toLowerCase();
    this.export(this.format, response => {
      const type = this.format === 'PDF' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      downloadBlob(new Blob([response.body], {type}),
        filenameFromResponse(response, 'Sammelmappe_' + this.data.folder.name + '.' + extension));
    });
  }

  private export(format: ExportFormat, onSuccess: (response) => void, onError?: () => void): void {
    this.exporting = true;
    this.folderService.exportFolder(this.data.folder.id, this.buildRequest(format))
      .pipe(finalize(() => this.exporting = false))
      .subscribe({
        next: onSuccess,
        error: (error: HttpErrorResponse) => {
          onError?.();
          this.notificationService.openSnackBar(folderErrorMessage(error, {
            400: 'Die Exporteinstellungen sind ungültig.'
          }));
        }
      });
  }

  private buildRequest(format: ExportFormat): FolderExportRequest {
    const fields: { [section in FolderExportSection]?: string[] } = {};
    this.sections.forEach(s => {
      // keep the order of the fields endpoint, it is the column order
      fields[s.section.section] = s.section.fields.map(f => f.key).filter(key => s.selected.has(key));
    });
    return {format, fields};
  }

  private entryCount(section: FolderExportSection): number {
    const info = FOLDER_ITEM_TYPES.find(t => t.exportSection === section);
    return info ? (this.data.folder[info.list]?.length ?? 0) : 0;
  }
}
